import type { RefObject } from 'react';
import { useNativeInputEvents } from '../../../hooks/useNativeInputEvents';
import { takeOverKey } from '../../../hooks/usePickerPopupKeyboard';

export interface DatePickerInputOptions {
  isOpen: boolean;
  /** Whether the field still shows the committed value, i.e. nothing has been typed. */
  showsCommittedValue: (input: HTMLInputElement) => boolean;
  focusCalendar: () => void;
  closeCalendar: () => void;
  commitTypedText: (text: string) => void;
  /** Moves the date by `days`, from the typed text when it is a date. */
  stepDay: (typedText: string, days: 1 | -1) => void;
}

const DAY_STEPS: Record<string, 1 | -1> = { ArrowUp: 1, ArrowDown: -1 };

export const handleDatePickerKeyDown = (
  event: KeyboardEvent,
  { isOpen, showsCommittedValue, focusCalendar, closeCalendar, stepDay }: DatePickerInputOptions,
) => {
  if (event.ctrlKey || event.metaKey) return;
  const input = event.currentTarget as HTMLInputElement;

  if (isOpen && event.key === 'Escape') {
    takeOverKey(event);
    closeCalendar();
    return;
  }

  const dayStep = DAY_STEPS[event.key];
  if (dayStep && !isOpen && !event.altKey && !event.shiftKey) {
    takeOverKey(event);
    stepDay(input.value, dayStep);
    return;
  }

  const opensCalendar =
    (event.key === 'ArrowDown' && (event.altKey || isOpen)) ||
    (!event.altKey && event.key === ' ') ||
    (!event.altKey && !isOpen && event.key === 'Enter' && showsCommittedValue(input));
  if (!opensCalendar) return;
  takeOverKey(event);
  focusCalendar();
};

export const useDatePickerInputEvents = (
  pickerRef: RefObject<{ nativeElement: HTMLElement | null } | null>,
  options: DatePickerInputOptions,
) => {
  useNativeInputEvents(pickerRef, {
    onKeyDown: (event) => handleDatePickerKeyDown(event, options),
    onBlur: (input) => options.commitTypedText(input.value),
  });
};
